/* eslint-disable import/prefer-default-export */
import * as React from 'react'
import cx from 'classnames'
import styles from '../../setup.steps.styl'
import TextBox from '@src/components/common/text.box'
import Select, { ValueType } from '@src/components/common/select'
import Button from '@src/components/common/button'
import { map, keys, isEmpty } from 'lodash'
import { getNewSL, SysLogsDict, isIP } from '../../helpers'
import type { SystemLog, SystemLogData } from '../../models'
import type { SyslogCredInfo } from '@src/endpoints/services/commonHdr.service'

export interface Props {
  logs: SysLogsDict
  onChange: ({ logs }: { logs: SysLogsDict }) => void
}

interface SelectOptions {
  value?: string
  label?: string
}

type SystemLogsComponent<T> = React.FC<T> & {
  isValidSysLog: (id: string, logs: SysLogsDict) => boolean
}

export const SystemLogs: SystemLogsComponent<Props> = (props: Props) => {
  function onAdd() {
    const { logs, onChange } = { ...props }
    onChange({ logs: { ...logs, ...getNewSL() } })
  }

  function onDelete(id: string) {
    const { logs } = { ...props }
    delete logs[id]

    const emptyLogs = keys(logs).length === 0
    if (emptyLogs) {
      props.onChange({ logs: { ...logs, ...getNewSL() } })
      return
    }
    props.onChange({ logs })
  }

  function handleChange(
    id: string,
    value: React.ReactText,
    name: keyof SystemLogData
  ) {
    const { logs, onChange } = { ...props }
    onChange({
      logs: {
        ...logs,
        [id]: {
          ...logs[id],
          data: {
            ...logs[id].data,
            [name]: value
          }
        }
      }
    })
  }

  function handleProtocol(id: string, data: ValueType<SelectOptions>) {
    const { value } = data as SelectOptions
    const { logs, onChange } = { ...props }
    onChange({
      logs: {
        ...logs,
        [id]: {
          ...logs[id],
          data: {
            ...logs[id].data,
            network: value
          }
        }
      }
    })
  }

  const isOnlyOneSysLog = (logs: SysLogsDict) => keys(logs).length === 1

  const validationIPAddr = (value?: string | number) => ({
    isValid: isEmpty(value) || isIP(String(value)),
    messages: 'Invalid ip address format'
  })

  function renderSystemLog({
    id,
    data: { ip, port, network, credentials },
    isVerify,
    isNew
  }: SystemLog) {
    const { logs } = { ...props }
    const networkValue = network
      ? { value: network, label: network }
      : (null as SelectOptions)
    const validationIP = validationIPAddr(ip)
    return (
      <React.Fragment key={id}>
        <div className={styles.fieldWrapper} role="syslog">
          <TextBox
            className={cx(styles.inputWrapper, styles.ipAddress)}
            type="text"
            name="ip"
            placeholder="IP address..."
            value={ip}
            notValidClassName={styles.error}
            validation={validationIPAddr}
            readOnly={false}
            onChange={(value, name, e) => handleChange(id, value, name)}
          />

          <TextBox
            className={cx(styles.inputWrapper, styles.port)}
            type="text"
            name="port"
            placeholder="Port..."
            value={port}
            readOnly={false}
            onChange={(value, name, e) => handleChange(id, value, name)}
          />

          <Select
            role={'network'}
            className={styles.selectWrapper}
            classNameItem={styles.selectItem}
            name="network"
            value={networkValue}
            onChange={data => handleProtocol(id, data)}
            placeholder={'Select Protocol...'}
            options={
              [
                { value: 'udp', label: 'udp' },
                { value: 'tcp', label: 'tcp' }
              ] as SelectOptions[]
            }
          />

          <Button
            role={`remove-syslog-btn`}
            className={styles.verifyButton}
            title="DELETE"
            icon={null}
            disabled={isNew ? isOnlyOneSysLog(logs) : false}
            style="primary"
            onClick={() => onDelete(id)}
          />
        </div>

        {!validationIP.isValid && (
          <div
            role={`error-messages`}
            className={cx(styles.errorMessages, styles.errorMessagesSysLog)}
          >
            {validationIP.messages}
          </div>
        )}
      </React.Fragment>
    )
  }

  const { logs } = { ...props }
  return (
    <div className={styles.sysLogConfigWrapper}>
      <div className={styles.content}>
        <div>SysLog Configuration</div>
        <div className={styles.actions}>
          <Button
            role={`add-syslog-btn`}
            className={styles.addSysLog}
            title=" + ADD"
            icon={null}
            style="primary"
            onClick={onAdd}
          />
        </div>
      </div>
      <div className={styles.sysLogs} role="syslogs">
        {map(logs, sl => renderSystemLog(sl))}
      </div>
    </div>
  )
}

SystemLogs.isValidSysLog = (id: string, logs: SysLogsDict) => {
  const checkSL = logs[id]
  const { data } = checkSL || ({} as SystemLog)
  const { ip, port, network, credentials } = data || ({} as SystemLogData)
  const { username, password } = credentials || ({} as SyslogCredInfo)
  const isValidCredentials = credentials
    ? !isEmpty(username) && !isEmpty(password)
    : true
  return (
    !isEmpty(ip) && !isEmpty(port) && !isEmpty(network) && isValidCredentials
  )
}
